import React, { useState, useEffect } from 'react';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import './LogModals.css';

interface Animal {
  id: number;
  name: string;
  category: string;
  count: number;
}

interface MilkLogModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const MilkLogModal: React.FC<MilkLogModalProps> = ({ onClose, onSuccess }) => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [selectedAnimal, setSelectedAnimal] = useState<number | null>(null);
  const [gallons, setGallons] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ points: number; message: string } | null>(null);

  useEffect(() => {
    loadAnimals();
  }, []);

  const loadAnimals = async () => {
    try {
      const data = await bridgeClubAPI.getAnimals();
      // Filter to dairy animals (goats, cattle)
      const dairyAnimals = data.filter((a: Animal) =>
        ['goat', 'cattle'].includes(a.category)
      );
      setAnimals(dairyAnimals);
      // Auto-select if only one animal
      if (dairyAnimals.length === 1) {
        setSelectedAnimal(dairyAnimals[0].id);
      }
    } catch (err) {
      setError('Failed to load animals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAnimal || !gallons) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await bridgeClubAPI.logMilkForPoints({
        animal_id: selectedAnimal,
        gallons: parseFloat(gallons),
      });

      if (response.success) {
        setSuccess({
          points: response.points_earned,
          message: response.message,
        });
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to log milk');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to log milk');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    return category === 'goat' ? '🐐' : '🐄';
  };

  const quickAmounts = [0.5, 1, 1.5, 2];

  return (
    <div className="log-modal-overlay" onClick={onClose}>
      <div className="log-modal" onClick={e => e.stopPropagation()}>
        <div className="log-modal-header">
          <span className="log-modal-icon">🥛</span>
          <h2>Log Milk</h2>
          <button className="log-modal-close" onClick={onClose}>&times;</button>
        </div>

        {success ? (
          <div className="log-success">
            <span className="success-icon">✅</span>
            <p className="success-points">+{success.points} points!</p>
            <p className="success-message">{success.message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {loading ? (
              <div className="log-loading">Loading...</div>
            ) : animals.length === 0 ? (
              <div className="log-empty">
                <p>No dairy animals found.</p>
                <p className="log-empty-hint">
                  Add goats or cattle to log milk production.
                </p>
              </div>
            ) : (
              <>
                {animals.length > 1 && (
                  <div className="form-group">
                    <label>Select Animal</label>
                    <div className="animal-select-grid">
                      {animals.map(animal => (
                        <button
                          key={animal.id}
                          type="button"
                          className={`animal-select-btn ${selectedAnimal === animal.id ? 'selected' : ''}`}
                          onClick={() => setSelectedAnimal(animal.id)}
                        >
                          <span className="animal-select-icon">{getCategoryIcon(animal.category)}</span>
                          <span className="animal-select-name">{animal.name || `${animal.category} #${animal.id}`}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="gallons">Gallons Today</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="gallons"
                    value={gallons}
                    onChange={e => setGallons(e.target.value)}
                    onBlur={e => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val > 0) setGallons(String(val));
                      else setGallons('');
                    }}
                    placeholder="How many gallons?"
                    required
                  />
                  <div className="quick-buttons">
                    {quickAmounts.map(amount => (
                      <button
                        key={amount}
                        type="button"
                        className="quick-btn"
                        onClick={() => setGallons(amount.toString())}
                      >
                        {amount} gal
                      </button>
                    ))}
                  </div>
                </div>

                {error && <div className="log-error">{error}</div>}

                <div className="log-modal-actions">
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={!selectedAnimal || !gallons || submitting}
                  >
                    {submitting ? 'Logging...' : 'Log Milk'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default MilkLogModal;
